import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Flex } from '@chakra-ui/react'
import { AssetPhotoState, OrderAssetState, SetOrderAssetRequest } from '@leid/api-assets'

import { AssetPhotoUploadRequest } from 'appTypes/api-requests/assets'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

import ManyAssetsReminderStep from './steps/ManyAssetsReminderStep'
import OpenDoorStep from './steps/OpenDoorsStep'
import PickupAssetStep from './steps/PickupAssetStep'
import ReturnAssetStep from './steps/ReturnAssetStep'
import TakePhotosStep from './steps/TakePhotosStep'
import {
  CommitStepData,
  FlowDirection,
  FlowStepDescription,
  ManyAssetsReminderStepData,
  OpenDoorStepData,
  PickUpAssetsStepData,
  ReturnAssetsStepData,
  SelectedAssetData,
  StepType,
  TakePhotosStepData,
} from './models'
import { TakePhotoStepImageData } from './utils/models'

type Props = {
  stepIndex: number
  activeStep: FlowStepDescription | undefined
  goToNextStep: () => void
  stopFlow: () => void
  setSelectedAssetVisualTag: (visualTag: string) => void
}

const PickupReturnFlow = (props: Props): JSX.Element => {
  const { stepIndex, activeStep, goToNextStep, stopFlow, setSelectedAssetVisualTag } = props

  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [selectedAsset, setSelectedAsset] = useState<SelectedAssetData | undefined>(undefined)
  const [images, setImages] = useState<TakePhotoStepImageData[]>([])

  useEffect(() => {
    if (activeStep?.type === StepType.COMMIT) {
      doCommit(activeStep.data as CommitStepData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep])

  const doCommit = async (stepData: CommitStepData) => {
    if (selectedAsset) {
      const request: SetOrderAssetRequest = {
        orderId: selectedAsset.orderId,
        orderItemId: selectedAsset.orderItemId,
        assetId: selectedAsset.assetId,
        locationId: selectedAsset.locationId,
        sublocationId: selectedAsset.sublocationId,
        timestamp: selectedAsset.timestamp,
        origin: selectedAsset.origin,
        state: selectedAsset.state,
        reason: selectedAsset.reason,
      }

      const response = await apiClient.setOrderAssetState(request)
      if (!response.success || !response.data) {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
        stopFlow()
        return
      }
    }

    if (images.length > 0) {
      for (const image of images) {
        const imageUploadRequest: AssetPhotoUploadRequest = {
          imageUrl: image.imageBlob,
          bucketName: 'asset-photos',
          state:
            stepData.flowDirection === FlowDirection.PICKUP ? AssetPhotoState.AT_PICKUP : AssetPhotoState.AT_DELIVERY,
          assetId: selectedAsset?.assetId, // Because first we update status of order item and only them upload photos
          productId: stepData.productId,
          customerId: stepData.customerId,
          orderId: stepData.orderId,
          orderItemId: stepData.orderItemId,
          imageSequenceStepId: image.sequenceStepId,
        }

        const response = await apiClient.uploadAssetImage(imageUploadRequest)
        if (!response.success || !response.data) {
          console.log(`Error uploading image: ${image.imageSrc}`, response)
        }
      }
    }

    goToNextStep()
  }

  const handleAssetSelect = (selectedAsset: SelectedAssetData): void => {
    setSelectedAsset(selectedAsset)

    if (selectedAsset.state === OrderAssetState.PICKEDUP) {
      setSelectedAssetVisualTag(selectedAsset.visualTag)
    }
  }

  return (
    <>
      {activeStep && (
        <Flex
          key={stepIndex}
          className={'page-transition'}
          flexDirection="column"
          justifyContent=" center"
          alignItems="center"
          gridGap="12px"
          width="100%"
          // maxH={'80vh'}
          overflow={'auto'}
        >
          {activeStep.type === StepType.OPEN_DOOR && (
            <OpenDoorStep data={activeStep.data as OpenDoorStepData} onStepSuccess={goToNextStep} />
          )}
          {activeStep.type === StepType.PICKUP_ASSET && (
            <PickupAssetStep
              data={activeStep.data as PickUpAssetsStepData}
              onStepSuccess={goToNextStep}
              setAssetChangeRequest={handleAssetSelect}
            />
          )}
          {activeStep.type === StepType.RETURN_ASSET && (
            <ReturnAssetStep
              data={activeStep.data as ReturnAssetsStepData}
              onStepSuccess={goToNextStep}
              setAssetChangeRequest={handleAssetSelect}
              stopFlow={stopFlow}
            />
          )}
          {activeStep.type === StepType.TAKE_PHOTOS && (
            <TakePhotosStep
              data={activeStep.data as TakePhotosStepData}
              onStepSuccess={goToNextStep}
              setCapturedImages={setImages}
            />
          )}
          {activeStep.type === StepType.MANY_ASSETS_REMINDER && (
            <ManyAssetsReminderStep
              data={activeStep.data as ManyAssetsReminderStepData}
              onStepSuccess={goToNextStep}
              setAssetChangeRequest={handleAssetSelect}
            />
          )}
          {activeStep.type === StepType.COMMIT && <Loader />}
        </Flex>
      )}
    </>
  )
}

export default PickupReturnFlow
